// Zapis przebiegu lekcji w trakcie (patch-34): po odświeżeniu strony (albo zamknięciu i ponownym otwarciu aplikacji) lekcja wraca na to samo zadanie
// i z tymi samymi wynikami, zamiast zaczynać od początku. Postęp dziecka (gwiazdki, słówka, seria) i tak zmienia się dopiero na końcu lekcji.
//
// Lista zadań jest deterministyczna (sessionV2.ts: te same ziarna), więc wystarczy zapamiętać numer zadania, wyniki i identyfikatory zadań —
// identyfikatory służą do sprawdzenia, że po odświeżeniu to nadal ta sama lekcja (treść z serwera mogła się w międzyczasie zmienić).
// Zapis dotyczy jednego profilu, jednego dnia kalendarzowego i jednej lekcji; każdy inny start go ignoruje, a koniec lub wyjście (✕) kasuje.
import type { Outcome, Task } from '../types.ts';
import type { KeyValueStore } from './progress.ts';

export const RUN_KEY = 'naative.run.v1';

/** Do której lekcji należy zapis. */
export interface RunKey {
  profile: string;
  date: string;
  mode: 'lesson' | 'review';
  /** Numer dnia lekcji; null w powtórce słówek i w lekcjach kursu v2. */
  day: number | null;
  /** Kurs v2: id lekcji (np. „a1-001”); brak = dawna lekcja albo powtórka. */
  lesson?: string;
}

export interface RunProgress {
  index: number;
  outcomes: Outcome[];
  /**
   * Ostatni NOWY krok (patch-49): pierwsze nieukończone zadanie. Dziecko, które cofnęło się „←”, wraca tu przyciskiem „→”; dalej nie da się przeskoczyć.
   * Brak w zapisie ze starszej wersji = `index` (wtedy dziecko jest na ostatnim kroku).
   */
  reached?: number;
}

export interface RunStore {
  /** Zapisany stan tej lekcji albo null (brak zapisu, inna lekcja, inny dzień, inna lista zadań). */
  resume(key: RunKey, taskIds: readonly string[]): (RunProgress & { reached: number }) | null;
  save(key: RunKey, taskIds: readonly string[], progress: RunProgress): void;
  clear(): void;
}

const KINDS: readonly Task['kind'][] = ['banter', 'speak', 'cloze', 'build', 'quiz', 'say', 'grammar'];
const PHASES = ['repeat', 'read', 'build', 'scene'];

/** Przepuszcza tylko poprawnie zbudowane wyniki (zapis mógł się uszkodzić albo pochodzić ze starszej wersji). */
function cleanOutcome(raw: unknown): Outcome | null {
  const o = raw as Partial<Record<keyof Outcome, unknown>> | null;
  if (!o || typeof o !== 'object' || typeof o.taskId !== 'string' || typeof o.ref !== 'string' || !KINDS.includes(o.kind as Task['kind'])) return null;
  const out: Outcome = { taskId: o.taskId, kind: o.kind as Task['kind'], ref: o.ref };
  if (typeof o.phase === 'string' && PHASES.includes(o.phase)) out.phase = o.phase as NonNullable<Outcome['phase']>;
  for (const k of ['stars', 'percent', 'attempts'] as const) if (typeof o[k] === 'number' && Number.isFinite(o[k])) out[k] = o[k] as number;
  for (const k of ['passed', 'correct', 'review'] as const) if (typeof o[k] === 'boolean') out[k] = o[k] as boolean;
  return out;
}

export function createRunStore(store: KeyValueStore | null): RunStore {
  const remove = (): void => {
    try {
      store?.removeItem?.(RUN_KEY);
    } catch {
      /* ignore */
    }
  };
  return {
    resume(key, taskIds) {
      let r: Record<string, unknown> | null = null;
      try {
        const raw = store?.getItem(RUN_KEY);
        r = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
      } catch {
        return null;
      }
      if (!r || typeof r !== 'object' || r.v !== 1) return null;
      if (r.profile !== key.profile || r.date !== key.date || r.mode !== key.mode || (r.day ?? null) !== key.day || (r.lesson ?? null) !== (key.lesson ?? null)) return null;
      const ids = r.taskIds;
      if (!Array.isArray(ids) || ids.length !== taskIds.length || ids.some((id, i) => id !== taskIds[i])) return null;
      const index = r.index;
      if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index >= taskIds.length) return null;
      if (!Array.isArray(r.outcomes)) return null;
      const outcomes = r.outcomes.map(cleanOutcome);
      if (outcomes.some((o) => o === null)) return null;
      const reached = typeof r.reached === 'number' && Number.isInteger(r.reached) && r.reached >= index && r.reached < taskIds.length ? r.reached : index;
      return { index, outcomes: outcomes as Outcome[], reached };
    },
    save(key, taskIds, progress) {
      try {
        store?.setItem(RUN_KEY, JSON.stringify({ v: 1, ...key, taskIds, index: progress.index, outcomes: progress.outcomes, reached: Math.max(progress.index, progress.reached ?? 0) }));
      } catch {
        /* pełna pamięć albo tryb prywatny: lekcja działa dalej, tylko bez wznowienia */
      }
    },
    clear: remove,
  };
}
